/**
 * Default Highcharts chart implementation
 *
 * @param {HTMLElement} container - The DOM element to render the chart in
 * @param {Array|Object} data - Chart data series
 * @param {Object} options - Additional chart options. When `autoScale: true`,
 *   the yAxis is allowed to float to the data range (no forced zero baseline)
 *   and area-chart fill is anchored to the axis minimum rather than zero.
 */
export default function createDefaultChart(container, data, options = {}) {
  // Format the chart data
  let seriesData = [];
  
  // Handle different data formats
  if (Array.isArray(data) && data.length > 0) {
    if (typeof data[0] === 'object' && 'name' in data[0] && 'data' in data[0]) {
      // Data is already in Highcharts series format
      seriesData = data;
    } else {
      // Data is a simple array, create a single series
      seriesData = [{
        name: options.seriesName || 'Series 1',
        data: data
      }];
    }
  } else if (data && typeof data === 'object') {
    // Data might be a complex object with series
    if (data.series) {
      seriesData = data.series;
    } else {
      // Create a single series with the data object
      seriesData = [{
        name: options.seriesName || 'Series 1',
        data: [data]
      }];
    }
  }
  
  // Create default chart configuration
  const chartConfig = {
    chart: {
      type: options.chartType || 'line',
      backgroundColor: 'transparent',
      style: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial'
      },
      zoomType: 'xy'
    },
    title: {
      text: options.title || null
    },
    xAxis: {
      type: options.xAxisType || 'category',
      title: {
        text: options.xAxisTitle || null
      }
    },
    yAxis: {
      title: {
        text: options.yAxisTitle || null
      }
    },
    plotOptions: {
      series: {
        animation: {
          duration: 500
        }
      }
    },
    series: seriesData,
    credits: {
      enabled: false
    },
    ...options
  };

  // autoScale: let yAxis float to the data range and anchor area-fill to that
  // minimum rather than zero. Applied after the options spread so it reliably
  // wins even when callers pass their own yAxis/plotOptions.
  if (options.autoScale) {
    chartConfig.yAxis = {
      ...(chartConfig.yAxis || {}),
      startOnTick: false,
      endOnTick: false
    };
    chartConfig.plotOptions = {
      ...(chartConfig.plotOptions || {}),
      area: {
        ...((chartConfig.plotOptions && chartConfig.plotOptions.area) || {}),
        threshold: null
      }
    };
  }
  
  // Create the Highcharts chart
  return Highcharts.chart(container, chartConfig);
}